import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import {
  FleetApiService,
  Fleet,
  FleetsResponse,
} from '../api/fleet-api.service';
import { AuthService } from '../auth/auth.service';

@Injectable({ providedIn: 'root' })
export class FleetStateService {
  private api = inject(FleetApiService);
  private auth = inject(AuthService);

  fleets = signal<Fleet[]>([]);
  isLoading = signal(false);
  isLoaded = signal(false);
  activeFleetId = signal<string | null>(localStorage.getItem('activeFleetId'));
  activeFleet = computed(() => {
    const id = this.activeFleetId();
    return this.fleets().find((f) => f._id === id) || null;
  });

  constructor() {
    // Keep localStorage in sync when selection changes
    effect(() => {
      const id = this.activeFleetId();
      if (id) localStorage.setItem('activeFleetId', id);
      else localStorage.removeItem('activeFleetId');
    });

    this.auth.isAuthenticated$
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((isAuthed) => {
        if (!isAuthed) {
          this.resetState();
          return;
        }
        this.loadFleets(true);
      });
  }

  loadFleets(force = false): void {
    if (this.isLoading() || (this.isLoaded() && !force)) return;
    this.isLoading.set(true);
    this.api.getFleets().subscribe({
      next: (res: FleetsResponse) => {
        const list = res.fleets || [];
        this.fleets.set(list);
        const currentActive = this.activeFleetId();
        if (list.length === 0) {
          if (currentActive) this.activeFleetId.set(null);
        } else if (
          !currentActive ||
          !list.some((f) => f._id === currentActive)
        ) {
          this.activeFleetId.set(list[0]._id);
        }
        // Mark loaded even if list is empty to avoid repeated fetches
        this.isLoaded.set(true);
      },
      error: () => {
        this.fleets.set([]);
        // Avoid tight retry loops from callers; allow manual retry
        this.isLoaded.set(false);
        this.isLoading.set(false);
      },
      complete: () => this.isLoading.set(false),
    });
  }

  ensureLoaded(): void {
    if (!this.isLoaded() && !this.isLoading()) {
      this.loadFleets();
    }
  }

  setActiveFleet(fleetId: string): void {
    this.activeFleetId.set(fleetId);
  }

  clearActiveFleet(): void {
    this.activeFleetId.set(null);
  }

  private resetState(): void {
    this.fleets.set([]);
    this.clearActiveFleet();
    this.isLoaded.set(false);
    this.isLoading.set(false);
  }
}
